import React, { useState, forwardRef } from 'react';
import { TextInput, Label, Textarea, Select } from 'flowbite-react';
import {
  IconEye,
  IconEyeOff,
  IconSearch,
  IconMail,
  IconUser,
  IconLock,
  IconCalendar,
  IconPhone,
  IconAlertCircle
} from '@tabler/icons-react';

/**
 * Input component - Form inputs with MatDash styling
 * Extends Flowbite inputs with custom theme and icons
 */
const Input = forwardRef(({
  label = '',
  type = 'text',
  error = '',
  helper = '',
  icon = null,
  rightIcon = null,
  required = false,
  disabled = false,
  fullWidth = false,
  className = '',
  inputClassName = '',
  ...props
}, ref) => {
  const baseClasses = `
    block px-4 py-2.5 text-sm text-gray-900
    bg-white rounded-lg border
    ${error
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary/30 focus:border-primary'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    ${icon ? 'pl-10' : ''}
    ${rightIcon ? 'pr-10' : ''}
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white
    transition-colors duration-200
    ${inputClassName}
  `;

  const wrapperClasses = `
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;

  return (
    <div className={wrapperClasses}>
      {label && (
        <Label
          htmlFor={props.id}
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            {icon}
          </div>
        )}

        <input
          ref={ref}
          type={type}
          className={baseClasses}
          disabled={disabled}
          required={required}
          {...props}
        />

        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightIcon}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <IconAlertCircle size={16} />
          {error}
        </p>
      )}

      {helper && !error && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {helper}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Password Input with visibility toggle
export const PasswordInput = forwardRef(({ label = 'Password', ...props }, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Input
      ref={ref}
      type={showPassword ? 'text' : 'password'}
      label={label}
      icon={<IconLock size={20} className="text-gray-400" />}
      rightIcon={
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          {showPassword ? <IconEyeOff size={20} /> : <IconEye size={20} />}
        </button>
      }
      {...props}
    />
  );
});

PasswordInput.displayName = 'PasswordInput';

// Search Input
export const SearchInput = forwardRef(({ placeholder = 'Search...', onSearch, ...props }, ref) => {
  const [value, setValue] = useState('');

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(value);
    }
  };

  return (
    <Input
      ref={ref}
      type="search"
      placeholder={placeholder}
      icon={<IconSearch size={20} className="text-gray-400" />}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyPress={handleKeyPress}
      {...props}
    />
  );
});

SearchInput.displayName = 'SearchInput';

// Email Input
export const EmailInput = forwardRef((props, ref) => {
  return (
    <Input
      ref={ref}
      type="email"
      label="Email"
      icon={<IconMail size={20} className="text-gray-400" />}
      placeholder="name@example.com"
      {...props}
    />
  );
});

EmailInput.displayName = 'EmailInput';

// Textarea Component
export const TextArea = forwardRef(({
  label = '',
  error = '',
  helper = '',
  required = false,
  disabled = false,
  rows = 4,
  fullWidth = false,
  className = '',
  textareaClassName = '',
  ...props
}, ref) => {
  const baseClasses = `
    block p-4 text-sm text-gray-900
    bg-white rounded-lg border resize-none
    ${error
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary/30 focus:border-primary'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white
    transition-colors duration-200
    ${textareaClassName}
  `;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <Label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <textarea
        ref={ref}
        rows={rows}
        className={baseClasses}
        disabled={disabled}
        required={required}
        {...props}
      />

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <IconAlertCircle size={16} />
          {error}
        </p>
      )}

      {helper && !error && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {helper}
        </p>
      )}
    </div>
  );
});

TextArea.displayName = 'TextArea';

// Select Component
export const SelectInput = forwardRef(({
  label = '',
  options = [],
  error = '',
  helper = '',
  required = false,
  disabled = false,
  fullWidth = false,
  className = '',
  selectClassName = '',
  placeholder = 'Select an option',
  ...props
}, ref) => {
  const baseClasses = `
    block px-4 py-2.5 text-sm text-gray-900
    bg-white rounded-lg border
    ${error
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary/30 focus:border-primary'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    dark:bg-gray-700 dark:border-gray-600 dark:text-white
    transition-colors duration-200
    ${selectClassName}
  `;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <Label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <select
        ref={ref}
        className={baseClasses}
        disabled={disabled}
        required={required}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <IconAlertCircle size={16} />
          {error}
        </p>
      )}

      {helper && !error && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {helper}
        </p>
      )}
    </div>
  );
});

SelectInput.displayName = 'SelectInput';

// Checkbox Component
export const Checkbox = forwardRef(({
  label = '',
  error = '',
  className = '',
  ...props
}, ref) => {
  return (
    <div className={className}>
      <label className="inline-flex items-center cursor-pointer">
        <input
          ref={ref}
          type="checkbox"
          className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded
                   focus:ring-primary focus:ring-2 dark:bg-gray-700
                   dark:border-gray-600"
          {...props}
        />
        {label && (
          <span className="ml-2 text-sm text-gray-900 dark:text-gray-300">
            {label}
          </span>
        )}
      </label>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

// Radio Button Component
export const RadioButton = forwardRef(({
  label = '',
  name,
  value,
  className = '',
  ...props
}, ref) => {
  return (
    <label className={`inline-flex items-center cursor-pointer ${className}`}>
      <input
        ref={ref}
        type="radio"
        name={name}
        value={value}
        className="w-4 h-4 text-primary bg-gray-100 border-gray-300
                 focus:ring-primary focus:ring-2 dark:bg-gray-700
                 dark:border-gray-600"
        {...props}
      />
      {label && (
        <span className="ml-2 text-sm text-gray-900 dark:text-gray-300">
          {label}
        </span>
      )}
    </label>
  );
});

RadioButton.displayName = 'RadioButton';

export default Input;